// Admin v2 — Helyszín kezelés (Figma ADM_03 + ADM_03b): táblázat, aktiválás/szüneteltetés, törlés,
// hozzáadás és szerkesztés nyitvatartással. Adatmodell: a régi VenueManagement venues-sémája.
// GPS (latitude/longitude): az app klubtérképe csak koordinátás helyszínt mutat — a cím alapján
// egy gombbal kitölthető (OpenStreetMap Nominatim), kézzel javítható.
// „Érdekelne”: a térképen a még nem partner helyszínekre jelzett érdeklődés (venue_interest) száma.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { MapPicker } from './MapPicker';
import { VenueImportModal } from './VenueImport';
import { fmtInt } from '../data';
import {
  Avatar, Badge, Button, ConfirmModal, Empty, Field, Input, Modal, PageHeader, Segmented, Spinner, Toggle, useToast,
} from '../ui';

const DAYS = [
  ['monday', 'Hétfő'], ['tuesday', 'Kedd'], ['wednesday', 'Szerda'], ['thursday', 'Csütörtök'],
  ['friday', 'Péntek'], ['saturday', 'Szombat'], ['sunday', 'Vasárnap'],
] as const;
type Day = (typeof DAYS)[number][0];
type Hours = Record<Day, { open: string; close: string; closed: boolean }>;

const DEFAULT_HOURS: Hours = {
  monday: { open: '16:00', close: '23:00', closed: false },
  tuesday: { open: '16:00', close: '23:00', closed: false },
  wednesday: { open: '16:00', close: '23:00', closed: false },
  thursday: { open: '16:00', close: '23:00', closed: false },
  friday: { open: '14:00', close: '02:00', closed: false },
  saturday: { open: '12:00', close: '02:00', closed: false },
  sunday: { open: '12:00', close: '20:00', closed: true },
};

interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
  pricePerHour: number;
  operatorId: string;
  operatorName: string;
  operatorAvatar: string | null;
  machineCount: number;
  tableCount: number;
  status: 'active' | 'inactive';
  openingHours: Hours;
  lat: number | null;
  lng: number | null;
  interest: number;
}

const num = (v: unknown): number | null => {
  const n = Number(v);
  return v === null || v === undefined || v === '' || !Number.isFinite(n) || n === 0 ? null : n;
};

/** Cím → koordináta (OpenStreetMap Nominatim, Magyarország). */
async function geocodeAddress(q: string): Promise<[number, number] | null> {
  const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=hu&q=${encodeURIComponent(q)}`,
    { headers: { Accept: 'application/json' } });
  const j = (await res.json()) as { lat: string; lon: string }[];
  return j[0] ? [Number(j[0].lat), Number(j[0].lon)] : null;
}

type Filter = 'all' | 'nogps' | 'interest';

function normHours(v: unknown): Hours {
  const h = (v ?? {}) as Partial<Hours>;
  const out = { ...DEFAULT_HOURS };
  DAYS.forEach(([d]) => { if (h[d]) out[d] = { ...out[d], ...h[d] }; });
  return out;
}

export default function VenuesPage() {
  const toast = useToast();
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Venue | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Venue | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [vs, bs, ints] = await Promise.all([
        getDocs(collection(db, 'venues')),
        getDocs(collection(db, 'boards')).catch(() => null),
        getDocs(collection(db, 'venue_interest')).catch(() => null),
      ]);
      const interest: Record<string, number> = {};
      ints?.docs.forEach((d) => { const id = d.data().venueId; if (id) interest[id] = (interest[id] ?? 0) + 1; });
      const boardCount: Record<string, number> = {};
      bs?.docs.forEach((d) => { const id = d.data().venueId; if (id) boardCount[id] = (boardCount[id] ?? 0) + 1; });
      const ops = new Map<string, { name: string; avatar: string | null }>();
      const list: Venue[] = [];
      for (const d of vs.docs) {
        const v = d.data();
        if (v.isActive === false) continue; // törölt (régi soft-delete)
        const opId = String(v.operatorId ?? '');
        if (opId && opId !== 'temp-operator-id' && !ops.has(opId)) {
          const u = await getDoc(doc(db, 'users', opId)).catch(() => null);
          const ud = u?.exists() ? u.data() : null;
          ops.set(opId, ud ? { name: ud.displayName || ud.username || ud.email || '', avatar: ud.avatarUrl || ud.photoURL || null } : { name: '', avatar: null });
        }
        const op = ops.get(opId);
        list.push({
          id: d.id,
          name: String(v.name ?? 'Névtelen helyszín'),
          address: String(v.address ?? ''),
          city: String(v.city ?? ''),
          pricePerHour: Number(v.pricePerHour ?? 0),
          operatorId: opId,
          operatorName: op?.name || String(v.operatorName ?? ''),
          operatorAvatar: op?.avatar ?? null,
          machineCount: Number(v.machineCount ?? 0),
          tableCount: boardCount[d.id] ?? Number(v.tableCount ?? 0),
          status: v.status === 'inactive' ? 'inactive' : 'active',
          openingHours: normHours(v.openingHours),
          lat: num(v.latitude ?? v.lat),
          lng: num(v.longitude ?? v.lng),
          interest: interest[d.id] ?? 0,
        });
      }
      list.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setVenues(list);
    } catch (e) {
      console.warn('[venues] betöltés', e);
      setVenues([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = (venues ?? [])
      .filter((v) => !s || [v.name, v.city, v.address, v.operatorName].some((f) => f.toLowerCase().includes(s)))
      .filter((v) => filter !== 'nogps' || v.lat === null || v.lng === null);
    return filter === 'interest' ? [...out].sort((a, b) => b.interest - a.interest || a.name.localeCompare(b.name, 'hu')) : out;
  }, [venues, q, filter]);
  const noGps = (venues ?? []).filter((v) => v.lat === null || v.lng === null).length;

  const toggleStatus = async (v: Venue) => {
    try {
      await updateDoc(doc(db, 'venues', v.id), { status: v.status === 'active' ? 'inactive' : 'active' });
      toast(v.status === 'active' ? 'Helyszín szüneteltetve.' : 'Helyszín aktiválva.');
      load();
    } catch {
      toast('A módosítás nem sikerült.', 'red');
    }
  };

  return (
    <div>
      <PageHeader
        title="Helyszín kezelés"
        sub="Helyszínek és üzemeltetőik kezelése"
        right={(
          <div className="flex gap-2">
            <Button kind="ghost" icon="UploadSimple" onClick={() => setImporting(true)}>Importálás CSV-ből</Button>
            <Button icon="Plus" onClick={() => setEditing('new')}>Helyszín hozzáadása</Button>
          </div>
        )}
      />

      <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés helyszín név, cím vagy üzemeltető alapján..." />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Segmented<Filter> value={filter} onChange={setFilter} options={[
          { value: 'all', label: 'Összes' },
          { value: 'nogps', label: `GPS nélkül${noGps ? ` (${noGps})` : ''}` },
          { value: 'interest', label: 'Érdeklődés szerint' },
        ]} />
        {noGps > 0 && <span className="text-[12.5px] text-[#d9a514]">{noGps} helyszín nem látszik az app térképén, mert nincs koordinátája.</span>}
      </div>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
        {venues === null ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                  <th className="pl-5 pr-4 h-[39px] font-semibold w-[18%]">Helyszín neve</th>
                  <th className="px-4 font-semibold w-[23%]">Cím</th>
                  <th className="px-4 font-semibold w-[16%]">Üzemeltető</th>
                  <th className="px-4 font-semibold w-[9%]">Térkép</th>
                  <th className="px-4 font-semibold w-[8%]" title="A térképen „Érdekelne” jelzések száma">Érdekelne</th>
                  <th className="px-4 font-semibold w-[7%]">Asztalok</th>
                  <th className="px-4 font-semibold w-[11%]">Státusz</th>
                  <th className="px-4 font-semibold w-[12%]">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {list.map((v) => (
                  <tr key={v.id} className="border-t border-[#31383D] h-[63px]">
                    <td className="pl-5 pr-4 text-[14px] font-semibold text-white">{v.name}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3] truncate max-w-[280px]">{[v.city, v.address].filter(Boolean).join(', ') || '—'}</td>
                    <td className="px-4">
                      {v.operatorName ? (
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar src={v.operatorAvatar} name={v.operatorName} size={30} />
                          <span className="text-[14px] font-semibold text-white truncate">{v.operatorName}</span>
                        </div>
                      ) : <span className="text-[14px] text-[#B8B8C3]">—</span>}
                    </td>
                    <td className="px-4">
                      {v.lat !== null && v.lng !== null
                        ? <a href={`https://www.openstreetmap.org/?mlat=${v.lat}&mlon=${v.lng}#map=17/${v.lat}/${v.lng}`} target="_blank" rel="noreferrer"><Badge tone="green">GPS ✓</Badge></a>
                        : <button type="button" onClick={() => setEditing(v)}><Badge tone="gray">Nincs GPS</Badge></button>}
                    </td>
                    <td className="px-4 text-[14px] font-semibold" style={{ color: v.interest ? '#00AFF2' : '#8a8f99' }}>{fmtInt(v.interest)}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{fmtInt(v.tableCount)}</td>
                    <td className="px-4"><Badge tone={v.status === 'active' ? 'green' : 'gray'}>{v.status === 'active' ? 'Aktív' : 'Inaktív'}</Badge></td>
                    <td className="px-4">
                      <div className="flex items-center gap-2">
                        <SquareBtn icon="PencilSimpleLine" title="Szerkesztés" onClick={() => setEditing(v)} />
                        <SquareBtn icon={v.status === 'active' ? 'PauseCircle' : 'PlayCircle'} tone={v.status === 'active' ? '#d9a514' : '#2bc48a'}
                          title={v.status === 'active' ? 'Szüneteltetés' : 'Aktiválás'} onClick={() => toggleStatus(v)} />
                        <SquareBtn icon="Trash" tone="#ef4444" title="Törlés" onClick={() => setDeleting(v)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <Empty>{venues.length ? 'Nincs a keresésnek megfelelő helyszín.' : 'Még nincs helyszín.'}</Empty>}
          </div>
        )}
      </div>

      {importing && venues && (
        <VenueImportModal existing={venues.map((v) => ({ id: v.id, name: v.name, city: v.city, address: v.address, lat: v.lat, lng: v.lng }))}
          onClose={() => setImporting(false)} onDone={load} />
      )}
      {editing && <VenueModal venue={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={load} />}
      {deleting && (
        <ConfirmModal
          title="Helyszín törlése"
          text={<>Biztosan törlöd a(z) <b className="text-white">{deleting.name}</b> helyszínt? A művelet nem vonható vissza.</>}
          confirmLabel="Törlés"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            try { await deleteDoc(doc(db, 'venues', deleting.id)); toast('Helyszín törölve.'); load(); } catch { toast('A törlés nem sikerült.', 'red'); }
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

function SquareBtn({ icon, tone, title, onClick }: { icon: string; tone?: string; title: string; onClick: () => void }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick}
      className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] hover:border-[#00AFF2]/50 transition"
      style={{ color: tone ?? '#B8B8C3' }}>
      <Icon name={icon} size={16} />
    </button>
  );
}

/* ---------- Hozzáadás / szerkesztés (ADM_03b) ---------- */
function VenueModal({ venue, onClose, onSaved }: { venue: Venue | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(venue?.name ?? '');
  const [city, setCity] = useState(venue?.city ?? '');
  const [price, setPrice] = useState(venue ? String(venue.pricePerHour || '') : '');
  const [address, setAddress] = useState(venue?.address ?? '');
  const [hours, setHours] = useState<Hours>(venue?.openingHours ?? DEFAULT_HOURS);
  const [lat, setLat] = useState(venue?.lat != null ? String(venue.lat) : '');
  const [lng, setLng] = useState(venue?.lng != null ? String(venue.lng) : '');
  const [geoBusy, setGeoBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const setDay = (d: Day, patch: Partial<Hours[Day]>) => setHours((h) => ({ ...h, [d]: { ...h[d], ...patch } }));

  const findCoords = async () => {
    setMsg('');
    if (!address.trim() && !city.trim()) return setMsg('Előbb add meg a címet és a várost.');
    setGeoBusy(true);
    try {
      const hit = await geocodeAddress([address.trim(), city.trim(), 'Magyarország'].filter(Boolean).join(', '))
        ?? await geocodeAddress([name.trim(), city.trim()].filter(Boolean).join(', '));
      if (!hit) setMsg('A cím alapján nem található koordináta — add meg kézzel (pl. Google Térképről).');
      else { setLat(hit[0].toFixed(6)); setLng(hit[1].toFixed(6)); toast('Koordináta kitöltve — ellenőrizd a térképen.'); }
    } catch {
      setMsg('A koordináta-keresés nem sikerült, próbáld újra vagy add meg kézzel.');
    } finally {
      setGeoBusy(false);
    }
  };
  const latN = Number(lat.replace(',', '.'));
  const lngN = Number(lng.replace(',', '.'));
  const hasCoords = lat.trim() !== '' && lng.trim() !== '' && Number.isFinite(latN) && Number.isFinite(lngN);

  const save = async () => {
    setMsg('');
    if ((lat.trim() || lng.trim()) && (!hasCoords || Math.abs(latN) > 90 || Math.abs(lngN) > 180)) {
      return setMsg('Érvénytelen koordináta. Formátum: 47.497913 és 19.040236.');
    }
    const p = Number(price.replace(/\s/g, ''));
    if (!name.trim() || !city.trim() || !address.trim()) return setMsg('A név, a város és a cím megadása kötelező.');
    if (!p || p <= 0) return setMsg('Adj meg egy pozitív óradíjat.');
    setBusy(true);
    const data = {
      name: name.trim(), city: city.trim(), address: address.trim(), pricePerHour: p, openingHours: hours,
      latitude: hasCoords ? latN : null, longitude: hasCoords ? lngN : null,
    };
    try {
      if (venue) await updateDoc(doc(db, 'venues', venue.id), data);
      else await addDoc(collection(db, 'venues'), { ...data, operatorId: '', isActive: true, status: 'active', createdAt: new Date().toISOString() });
      toast(venue ? 'Helyszín mentve.' : 'Helyszín hozzáadva.');
      onSaved();
      onClose();
    } catch (e) {
      console.warn('[venues] mentés', e);
      setMsg('A mentés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={venue ? 'Helyszín szerkesztése' : 'Helyszín hozzáadása'}
      onClose={onClose}
      width={860}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button onClick={save} disabled={busy}>Mentés</Button></>}
    >
      <div className="flex flex-col gap-4">
        <Field label="Helyszín neve"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. Dart Club Budapest" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Város *"><Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Budapest" /></Field>
          <Field label="Ár/óra (Ft) *">
            <Input inputMode="numeric" value={price ? fmtInt(Number(price.replace(/\s/g, '')) || 0) : ''}
              onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))} placeholder="4 500" />
          </Field>
        </div>
        <Field label="Cím"><Input icon="MapPin" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Utca, házszám" /></Field>
        <div>
          <span className="block pb-1.5 text-[13px] font-semibold text-white">Hely a térképen</span>
          {/* kattints a térképre vagy húzd a jelölőt — a mezők automatikusan kitöltődnek */}
          <MapPicker lat={hasCoords ? latN : null} lng={hasCoords ? lngN : null}
            onPick={(a, b) => { setLat(String(a)); setLng(String(b)); setMsg(''); }} height={340} />
          <p className="pt-1.5 pb-3 text-[12px] text-[#8a8f99]">Kattints a klub helyére, vagy húzd a kék jelölőt. A „Cím alapján” gomb a címhez ugrik.</p>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <Field label="Szélesség (lat)"><Input inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="47.497913" /></Field>
            <Field label="Hosszúság (lng)"><Input inputMode="decimal" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="19.040236" /></Field>
            <Button kind="ghost" icon="MapPin" onClick={findCoords} disabled={geoBusy} className="whitespace-nowrap shrink-0 h-[44px]">{geoBusy ? 'Keresés…' : 'Cím alapján'}</Button>
          </div>
          <p className="pt-1.5 text-[12px] text-[#8a8f99]">
            A klubtérkép az appban csak koordinátás helyszínt mutat.{' '}
            {hasCoords && <a className="text-[#00AFF2]" href={`https://www.openstreetmap.org/?mlat=${latN}&mlon=${lngN}#map=17/${latN}/${lngN}`} target="_blank" rel="noreferrer">Megnézem a térképen ›</a>}
          </p>
        </div>
      </div>

      <h4 className="mt-6 mb-3 text-[15px] font-extrabold italic uppercase text-white">Nyitvatartás</h4>
      <div className="flex flex-col gap-2.5">
        {DAYS.map(([d, label]) => {
          const h = hours[d];
          return (
            <div key={d} className="grid grid-cols-[100px_1fr_auto_1fr_auto] items-center gap-3 min-h-[40px]">
              <span className="text-[13px] font-semibold text-white">{label}</span>
              {h.closed ? (
                <span className="col-span-3 text-[13px] italic text-[#8a8f99]">Zárva</span>
              ) : (
                <>
                  <input type="time" value={h.open} onChange={(e) => setDay(d, { open: e.target.value })}
                    className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white outline-none focus:border-[#00AFF2]/70 [color-scheme:dark]" />
                  <span className="text-[#8a8f99]">–</span>
                  <input type="time" value={h.close} onChange={(e) => setDay(d, { close: e.target.value })}
                    className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white outline-none focus:border-[#00AFF2]/70 [color-scheme:dark]" />
                </>
              )}
              <Toggle checked={!h.closed} onChange={(v) => setDay(d, { closed: !v })} />
            </div>
          );
        })}
      </div>
      {msg && <div className="mt-4 text-[13px] text-[#ef4444]" role="alert">{msg}</div>}
    </Modal>
  );
}
